import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Action, Store } from '@ngrx/store';
import { Router } from '@angular/router';
import { catchError, filter, map, switchMap } from 'rxjs/operators';
import { Observable, of } from 'rxjs';

import { EngageService } from '../engage/engage.service';
import { CalendarService } from '../calendar/calendar.service';
import * as calendar from '../actions/calendar';
import * as user from '../actions/user';
import * as engagement from '../actions/engagement';
import * as campaign from '../actions/campaign-engage';
import * as fromRoot from '../reducers';
import { MeetService } from '../meet/meet.service';
import {
  CampaignType,
} from 'spotlight-ui/app/models/engage/campaign-details.model';
import { AutomationSource } from 'spotlight-ui/app/discover/dependencies/automationsSource';
import {
  DigitalMarketingAutomationSource
} from 'spotlight-ui/app/discover/dependencies/digital-marketing-automation-source';
import { HttpClient } from '@angular/common/http';
import { CampaignDetailsService } from 'spotlight-ui/app/engage/campaign-details/campaign-details.service';

@Injectable()
export class CalendarEffects {
  public clearCalendarSearchOnLocationSwitch: Observable<Action> = createEffect(() => { return this.actions$.pipe(
    ofType(user.ActionTypes.GET_LOCATION_DETAILS_SUCCESS),
    switchMap(() => of(new calendar.CalendarSwitchLocations())),
  )});

  public fetchCalendarEvents$: Observable<Action> = createEffect(() => { return this.actions$.pipe(
    ofType(
      engagement.ActionTypes.SEND_COMMUNITY_CARE_EVENT_SUCCESS,
      engagement.ActionTypes.STOP_COMMUNITY_CARE_EVENT_SUCCESS,
      engagement.ActionTypes.SEND_THIRD_PARTY_PROMOTION_SUCCESS,
      engagement.ActionTypes.SEND_FREE_IN_CART_SUCCESS,
      engagement.ActionTypes.CANCEL_THIRD_PARTY_PROMOTION_SUCCESS,
      calendar.ActionTypes.CALENDAR_DATES_CHANGED,
      calendar.ActionTypes.CALENDAR_REFRESH_REQUESTED,
      calendar.ActionTypes.CALENDAR_SWITCH_LOCATIONS
    ),
    filter(() => this.router.url.split('?')[0].endsWith('/calendar')),
    switchMap(() => this.store.select(fromRoot.getCalendarEventsPayload)),
    map(( payload) => {
      this.calendarService.populateCalendarState(payload)
      return of()
    }),
    concatLatestFrom(() => this.store.select(fromRoot.getSelectedLocation)),
    map(([res, location]) => new calendar.FetchCalendarEventsSuccess(res, location.locationNumber)),
    catchError((error) => {
      return of(new calendar.FetchCalendarEventsFailure(error));
    })
  )});

  public fetchRewardDetails$: Observable<Action> = createEffect(() => { return this.actions$.pipe(
    ofType(calendar.ActionTypes.FETCH_REWARD_DETAILS),
    map((action: any) => action.payload),
    switchMap((engagementDetails) => this.engageService.getCalendarRewardDetails(
      engagementDetails.id,
      engagementDetails.type,
    )),
    map((res) => new calendar.FetchRewardDetailsSuccess(res)),
    catchError((error) => {
      return of(new calendar.FetchRewardDetailsFailure(error));
    })
  )});

  public fetchCampaignDetails$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(campaign.ActionTypes.FETCH_CAMPAIGN_DETAILS),
      map((action: any) => action.payload),
      filter((engagementDetails) => {
        return engagementDetails.type != CampaignType.Automation
      }),
      switchMap((engagementDetails) =>
        this.engageService.getCalendarCampaignDetails(engagementDetails.id).pipe(
          switchMap((campaignDetails) => {
            if (campaignDetails.campaignDetails.audienceID) {
              return this.meetService.getAudience(campaignDetails.campaignDetails.audienceID).pipe(
                map((audienceDetails) => ({ ...campaignDetails, ...audienceDetails }))
              )
            } else {
              return of(campaignDetails)
            }
          }),
          map((combinedDetails) => new campaign.FetchCampaignDetailsSuccess(combinedDetails)),
          catchError((error) => of(new campaign.FetchCampaignDetailsFailure(error)))
        )
      )
    );
  });

  public fetchAutomatedCampaignDetails$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(campaign.ActionTypes.FETCH_CAMPAIGN_DETAILS),
      map((action: any) => action.payload),
      filter((engagementDetails) => engagementDetails.type == CampaignType.Automation),
      concatLatestFrom(() => this.store.select(fromRoot.getSelectedLocation)),
      switchMap(([engagementDetails, location]) =>
        this.automationsSource.getAutomationByIdAllDetails(location.locationNumber, engagementDetails.id)
      ),
      switchMap((mappedDetails) => {
        return mappedDetails.automationDetails.campaigns?.length > 0 ? this.engageService.handleRecipientStatusesMultipleCampaigns(
          mappedDetails.locationNumber,
          mappedDetails.automationDetails.campaigns)
          .pipe(
            map(details => {
              this.campaignDetailsService.setCampaignRecipients(details)
              return { ...details, ...mappedDetails }
            })
          ) : of(mappedDetails)
      }),
      map((mappedDetails) => {
        return new campaign.FetchAutomationDetailsSuccess(mappedDetails)
      }),
      catchError((error) => of(new campaign.FetchCampaignDetailsFailure(error)))
    );
  });

  public fetchAutomatedRewardDetails$: Observable<Action> = createEffect(() => { return this.actions$.pipe(
    ofType(calendar.ActionTypes.FETCH_AUTOMATED_REWARD_DETAILS),
    map((action: any) => action.payload),
    switchMap((engagementDetails) => this.engageService.getCalendarAutomatedRewardDetails(
      engagementDetails.batchIds
    )),
    map((res) => new calendar.FetchAutomatedRewardDetailsSuccess(res)),
    catchError((error) => {
      return of(new calendar.FetchAutomatedRewardDetailsFailure(error));
    })
  )});

  public fetchEventDetails$: Observable<any> = createEffect(() => { return this.actions$.pipe(
    ofType(calendar.ActionTypes.FETCH_EVENT_DETAILS),
    map((action: any) => action.payload),
    switchMap((engagementDetails) => this.engageService.getCalendarEventDetails(engagementDetails.id).pipe(
      map((res) => new calendar.FetchEventDetailsSuccess(res)),
      catchError((error) => {
        return of(new calendar.FetchEventDetailsFailure(error));
      })
    ))
  )});

  public fetchEmailDetails$: Observable<any> = createEffect(() => { return this.actions$.pipe(
    ofType(calendar.ActionTypes.FETCH_EMAIL_DETAILS),
    map((action: any) => action.payload),
    switchMap((engagementDetails) => this.engageService.getCalendarEmailDetails(engagementDetails.id).pipe(
      map((res) => new calendar.FetchEmailDetailsSuccess(res)),
      catchError((error) => {
        return of(new calendar.FetchEmailDetailsFailure(error));
      })
    ))
  )});

  public fetchBonusPointDetails$: Observable<any> = createEffect(() => { return this.actions$.pipe(
    ofType(calendar.ActionTypes.FETCH_BONUS_POINT_DETAILS),
    map((action: any) => action.payload),
    switchMap((engagementDetails) => this.engageService.getCalendarBonusPointDetails(engagementDetails.id).pipe(
      map((res) => new calendar.FetchBonusPointDetailsSuccess(res)),
      catchError((error) => {
        return of(new calendar.FetchBonusPointDetailsFailure(error));
      })
    ))
  )});

  public fetchBonusPointStats$: Observable<any> = createEffect(() => { return this.actions$.pipe(
    ofType(calendar.ActionTypes.FETCH_BONUS_POINT_DETAILS),
    map((action: any) => action.payload),
    switchMap((engagementDetails) => this.engageService.getCalendarBonusPointStats(engagementDetails.id).pipe(
      map((res) => new calendar.FetchBonusPointStatsSuccess(res)),
      catchError((error) => {
        return of(new calendar.FetchBonusPointStatsFailure(error));
      })
    ))
  )});

  public fetchCommunityCareDetails$: Observable<any> = createEffect(() => { return this.actions$.pipe(
    ofType(calendar.ActionTypes.FETCH_COMMUNITY_CARE_DETAILS),
    map((action: any) => action.payload),
    switchMap((engagementDetails) => this.engageService.getCalendarCommunityCareEvent(engagementDetails.id).pipe(
      map((res) => new calendar.FetchCommunityCareDetailsSuccess(res)),
      catchError((error) => {
        return of(new calendar.FetchCommunityCareDetailsFailure(error));
      })
    ))
  )});

  public fetchThirdPartyDetails$: Observable<any> = createEffect(() => { return this.actions$.pipe(
    ofType(calendar.ActionTypes.FETCH_THIRD_PARTY_DETAILS),
    map((action: any) => action.payload),
    switchMap((engagementDetails) => this.engageService.getCalendarThirdPartyPromotion(engagementDetails.id).pipe(
      map((res) => new calendar.FetchThirdPartyDetailsSuccess(res)),
      catchError((error) => {
        return of(new calendar.FetchThirdPartyDetailsFailure(error));
      })
    ))
  )});

  public fetchFreeInCartDetails$: Observable<any> =createEffect(() => { return this.actions$.pipe(
    ofType(calendar.ActionTypes.FETCH_FREE_ITEM_IN_CART_DETAILS),
    map((action: any) => action.payload),
    switchMap((engagementDetails) => this.engageService.getCalendarFreeItemInCartPromotion(engagementDetails.id).pipe(
      map((res) => new calendar.FetchFreeItemInCartDetailsSuccess(res)),
      catchError((error) => {
        return of(new calendar.FetchFreeItemInCartDetailsFailure(error));
      })
    ))
  )});

  public fetchFreeItemInCartStats$: Observable<any> = createEffect(() => { return this.actions$.pipe(
    ofType(calendar.ActionTypes.FETCH_FREE_ITEM_IN_CART_DETAILS),
    map((action: any) => action.payload),
    switchMap((engagementDetails) => this.engageService.getCalendarFreeInCartStats(engagementDetails.id).pipe(
      map((res) => new calendar.FetchFreeItemInCartStatsSuccess(res)),
      catchError((error) => {
        return of(new calendar.FetchFreeItemInCartStatsFailure(error));
      })
    ))
  )});

  public fetchThirdPartyStats$: Observable<any> = createEffect(() => { return this.actions$.pipe(
    ofType(calendar.ActionTypes.FETCH_THIRD_PARTY_DETAILS),
    map((action: any) => action.payload),
    switchMap((engagementDetails) => this.engageService.getCalendarThirdPartyPromotionStats(engagementDetails.id).pipe(
      map((res) => new calendar.FetchThirdPartyStatsSuccess(res)),
      catchError((error) => {
        return of(new calendar.FetchThirdPartyStatsFailure(error));
      })
    ))
  )});

  constructor(
    private actions$: Actions,
    private engageService: EngageService,
    private calendarService: CalendarService,
    private readonly meetService: MeetService,
    private readonly campaignDetailsService: CampaignDetailsService,
    private router: Router,
    private readonly http: HttpClient,
    private readonly store: Store<fromRoot.State>,
  ) { }

  private readonly automationsSource: AutomationSource = new DigitalMarketingAutomationSource(this.http);

}
